import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, MessageCircle, ShieldCheck, ShoppingCart, Truck } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { api, imgUrl, preorderLabel, usd } from "@/lib/api";
import { useLang } from "@/i18n";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();
  const { t, lang } = useLang();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6 pt-24">
        <div data-testid="product-not-found" className="text-center">
          <p className="font-display text-3xl text-white mb-4">{t("product_not_found")}</p>
          <Link
            data-testid="product-not-found-shop-link"
            to="/shop"
            className="inline-block bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium px-8 py-3.5 transition-colors duration-300"
          >
            {t("back_shop")}
          </Link>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
        <div className="h-96 bg-white/[0.03] border border-white/10 animate-pulse" />
      </main>
    );
  }

  return (
    <main data-testid="product-detail-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={20}>
        <Link
          data-testid="back-to-shop-link"
          to="/shop"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#FFC61A] transition-colors duration-300 mb-10"
        >
          <ArrowLeft size={15} /> {t("product_back")}
        </Link>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <div className="relative border border-white/10 overflow-hidden group">
            <img
              data-testid="product-detail-image"
              src={imgUrl(product.image)}
              alt={product.name}
              className="w-full h-[420px] lg:h-[520px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            {product.featured && (
              <span className="absolute top-4 left-4 bg-[#ED1C24] text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1">
                Featured
              </span>
            )}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#ED1C24]" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">
            {product.category}{product.brand ? ` — ${product.brand}` : ""}
          </p>
          <h1 className="font-display font-semibold text-white text-3xl sm:text-5xl tracking-tight leading-tight mb-6">
            {product.name}
          </h1>
          <p data-testid="product-detail-price" className="font-display text-4xl text-[#FFC61A] mb-8">
            {usd(product.price)}
          </p>
          <p className="text-slate-400 leading-relaxed mb-8">
            {lang === "tet" && product.description_tet ? product.description_tet : product.description}
          </p>

          <div className="flex items-center gap-3 mb-10 flex-wrap">
            {product.availability === "preorder" && (
              <span
                data-testid="product-preorder-badge"
                className="text-xs tracking-[0.1em] uppercase px-4 py-2 border border-[#FFC61A]/40 text-[#FFC61A]"
              >
                {preorderLabel(product, t)}
              </span>
            )}
            {(() => {
              const status = product.stock_status || (product.stock > 5 ? "in_stock" : product.stock > 0 ? "low_stock" : "out_of_stock");
              const styles = {
                in_stock: "border-emerald-500/40 text-emerald-400",
                low_stock: "border-[#FFC61A]/40 text-[#FFC61A]",
                out_of_stock: "border-[#ED1C24]/40 text-[#ED1C24]",
              };
              const labels = {
                in_stock: t("in_stock"),
                low_stock: product.stock > 0 ? `${t("low_stock")} — ${product.stock} ${t("product_low_left")}` : t("low_stock"),
                out_of_stock: t("out_stock"),
              };
              return (
                <span
                  data-testid="product-stock-badge"
                  className={`text-xs tracking-[0.1em] uppercase px-4 py-2 border ${styles[status]}`}
                >
                  {labels[status]}
                </span>
              );
            })()}
          </div>

          <div className="flex flex-wrap gap-4 mb-10">
            <button
              data-testid="product-detail-add-to-cart"
              onClick={() => {
                add(product);
                toast.success(`${product.name} ${t("cart_added")}`);
              }}
              className="inline-flex items-center gap-3 bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium px-8 py-4 transition-all duration-300 hover:shadow-[0_0_30px_rgba(237,28,36,0.4)]"
            >
              <ShoppingCart size={17} /> {t("add_cart")}
            </button>
            <a
              data-testid="product-detail-whatsapp"
              href={`https://wa.me/67077138966?text=${encodeURIComponent(
                `Hello KINOS, I want to buy: ${product.name} (${usd(product.price)})`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 font-medium px-8 py-4 transition-colors duration-300"
            >
              <MessageCircle size={17} /> {t("buy_whatsapp")}
            </a>
            {product.catalogue && (
              <a
                data-testid="product-catalogue-download"
                href={imgUrl(product.catalogue)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 border border-white/20 text-slate-200 hover:border-[#FFC61A] hover:text-[#FFC61A] font-medium px-8 py-4 transition-colors duration-300"
              >
                <FileDown size={17} /> {t("download_catalogue")}
              </a>
            )}
          </div>

          <div className="border-t border-white/10 pt-6 space-y-3 text-sm text-slate-400">
            <p className="flex items-center gap-3">
              <ShieldCheck size={16} className="text-[#FFC61A]" /> {t("product_warranty")}
            </p>
            <p className="flex items-center gap-3">
              <Truck size={16} className="text-[#FFC61A]" /> {t("product_delivery")}
            </p>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
